import React from 'react';
import styled from 'styled-components';

const Spacer = styled.div`
    max-height: 100%;
    width: 100%;
`;

/**
 * Section 96px -> 56px
 * Used commonly between sections
 */
export const SectionSpacer = styled(Spacer)`
    height: ${(p) => p.theme.spacing(12)}px;
    ${(p) => p.theme.breakpoints.down('md')} {
        height: ${(p) => p.theme.spacing(9)}px;
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        height: ${(p) => p.theme.spacing(7)}px;
    }
`;

/**
 * Feature 72px -> 56px
 * Used commonly inside of sections
 */
export const FeatureSpacer = styled(Spacer)`
    height: ${(p) => p.theme.spacing(9)}px;
    ${(p) => p.theme.breakpoints.down('md')} {
        height: ${(p) => p.theme.spacing(8)}px;
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        height: ${(p) => p.theme.spacing(7)}px;
    }
`;

/**
 * Layout 128px -> 40px
 * Used commonly between larger elements
 */
export const LayoutSpacer = styled(Spacer)`
    height: ${(p) => p.theme.spacing(16)}px;
    ${(p) => p.theme.breakpoints.down('md')} {
        height: ${(p) => p.theme.spacing(8)}px;
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        height: ${(p) => p.theme.spacing(7)}px;
    }
`;

/**
 * Heading 32px -> 24px
 * Used commonly above headings
 */
export const HeadingSpacer = styled(Spacer)`
    height: ${(p) => p.theme.spacing(4)}px;
    ${(p) => p.theme.breakpoints.down('sm')} {
        height: ${(p) => p.theme.spacing(3)}px;
    }
`;

/**
 * Eyebrow 16px
 * Used commonly below eyebrows
 */
export const EyebrowSpacer = styled(Spacer)`
    height: ${(p) => p.theme.spacing(2)}px;
`;

/**
 * Paragraph 8px
 * Used commonly above paragraphs
 */
export const ParagraphSpacer = styled(Spacer)`
    height: ${(p) => p.theme.spacing(1)}px;
`;

/**
 * Custom
 * Manually pass in the spacing
 */
export const CustomSpacer = styled.div<{spacing?: number; downSmSpacing?: number}>`
    height: ${(p) => p.theme.spacing(typeof p.spacing !== 'undefined' ? p.spacing : 0)}px;
    ${(p) => p.theme.breakpoints.down('sm')} {
        height: ${(p) =>
            p.theme.spacing(
                typeof p.downSmSpacing !== 'undefined' ? p.downSmSpacing : p.spacing || 0
            )}px;
    }
`;

export type VerticalSpacerOptions =
    | 'section'
    | 'layout'
    | 'heading'
    | 'eyebrow'
    | 'paragraph'
    | 'feature'
    | 'custom';

export interface VerticalSpacerProps {
    variant: VerticalSpacerOptions;
    spacing?: number;
    downSmSpacing?: number;
}

export const VerticalSpacer = ({
    variant = 'paragraph',
    spacing,
    downSmSpacing
}: VerticalSpacerProps): JSX.Element => {
    let spacerEl;
    switch (variant) {
        case 'section':
            spacerEl = <SectionSpacer className="ue-spacer section" />;
            break;
        case 'heading':
            spacerEl = <HeadingSpacer className="ue-spacer heading" />;
            break;
        case 'layout':
            spacerEl = <LayoutSpacer className="ue-spacer layout" />;
            break;
        case 'eyebrow':
            spacerEl = <EyebrowSpacer className="ue-spacer eyebrow" />;
            break;
        case 'paragraph':
            spacerEl = <ParagraphSpacer className="ue-spacer paragraph" />;
            break;
        case 'feature':
            spacerEl = <FeatureSpacer className="ue-spacer feature" />;
            break;
        case 'custom':
            spacerEl = (
                <CustomSpacer
                    className="ue-spacer custom"
                    spacing={spacing}
                    downSmSpacing={downSmSpacing}
                />
            );
            break;
        default:
            spacerEl = <HeadingSpacer className="ue-spacer heading" />;
            break;
    }

    return spacerEl;
};
